import { Injectable } from '@angular/core';
import { InvoicesResource } from '../model/model-resource';
import { Invoice as LegacyInvoice } from '../model/invoice';
import { map, take, tap } from 'rxjs/operators';
import { Observable, ReplaySubject } from 'rxjs';
import { InvoicesService } from '../api/services';
import { Invoice, InvoiceWorkflowEvent } from '../api/models';

@Injectable()
export class InvoicePeerService {
  // New API invoice
  private invoice: ReplaySubject<Invoice>;
  // Old Asp.NET API invoice
  private legacyInvoice: ReplaySubject<LegacyInvoice>;
  private state: ReplaySubject<string>;

  constructor(
    private invoicesResource: InvoicesResource,
    private invoicesService: InvoicesService
  ) {
    this.legacyInvoice = new ReplaySubject<LegacyInvoice>(1);
    this.invoice = new ReplaySubject<Invoice>(1);
    this.state = new ReplaySubject<string>(1);
  }

  initialize(id: number): void {
    this.invoicesService.getInvoice({ id: id }).subscribe((invoice: Invoice) => {
      this.invoiceChanged(invoice);
    });
  }

  /**
   *
   * @returns Current invoice (new API)
   */
  getInvoice(): Observable<Invoice> {
    return this.invoice.asObservable();
  }

  /**
   * @deprecated
   * @returns Current invoice (old API)
   */
  get(): Observable<LegacyInvoice> {
    return this.legacyInvoice.asObservable();
  }

  /**
   * @returns Current invoice editing state (locked/unlocked)
   */
  getState(): Observable<string> {
    return this.state.asObservable();
  }

  lock(): void {
    this.state.next('locked');
  }

  unlock(): void {
    this.state.next('unlocked');
  }

  /**
   * Re-reads the current invoice, as initialize does, by the id of the
   * modern invoice. Not by the legacy invoice's: the legacy stream carries
   * null for an invoice the legacy app cannot serve (a light-entrepreneur
   * invoice, or any invoice on a local stack without the legacy app), and
   * reading its id threw.
   */
  refresh(): void {
    this.getInvoice()
      .pipe(take(1))
      .subscribe((invoice) => {
        this.initialize(invoice.id);
      });
  }

  /**
   * Submits an accept workflow event. Returns the request observable, as
   * resetState does, so the caller can surface a refusal (e.g. a 422
   * financing_on_hold listing the holds). The current invoice is refreshed on
   * success.
   */
  acceptState(invoice: Invoice, reason: string): Observable<Invoice> {
    const evt: InvoiceWorkflowEvent = {
      name: 'accept',
      reason: reason,
    };
    return this.invoicesService
      .createWorkflowEvent({
        'If-Match': invoice.etag,
        id: invoice.id,
        body: evt,
      })
      .pipe(tap((updated: Invoice) => this.invoiceChanged(updated)));
  }

  /**
   * Submits a reject workflow event. Returns the request observable, as
   * acceptState does. The current invoice is refreshed on success.
   */
  rejectState(invoice: Invoice, reason: string): Observable<Invoice> {
    const evt: InvoiceWorkflowEvent = {
      name: 'reject',
      reason: reason,
    };
    return this.invoicesService
      .createWorkflowEvent({
        'If-Match': invoice.etag,
        id: invoice.id,
        body: evt,
      })
      .pipe(tap((updated: Invoice) => this.invoiceChanged(updated)));
  }

  /**
   * Submits a reset workflow event. Returns the request observable so the
   * caller can surface backend errors (e.g. 403 missing permission, 422
   * missing reason) instead of swallowing them. The current invoice is
   * refreshed on success.
   */
  resetState(invoice: Invoice, reason: string): Observable<Invoice> {
    const evt: InvoiceWorkflowEvent = {
      name: 'reset',
      reason: reason,
    };
    return this.invoicesService
      .createWorkflowEvent({
        'If-Match': invoice.etag,
        id: invoice.id,
        body: evt,
      })
      .pipe(tap((updated: Invoice) => this.invoiceChanged(updated)));
  }

  generateFinalReport(id: number, emailRecipients: string | null): void {
    this.invoicesResource
      .generateFinalReport(id, emailRecipients)
      .subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
      });
  }

  generateInvoiceNumber(id: number): void {
    this.invoicesResource
      .generateInvoiceNumber(id)
      .subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
      });
  }

  sendInvoice(id: number): void {
    this.invoicesResource.sendInvoice(id).subscribe(
      (updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
      },
      (err: Error) => {
        console.log(err);
      }
    );
  }

  sendModifiedOriginalInvoice(id: number): void {
    this.invoicesResource
      .sendModifiedOriginalInvoice(id)
      .subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
      });
  }

  sendToAccounting(id: number): void {
    this.invoicesResource.sendToAccounting(id).subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
    });
  }

  /**
   * @deprecated Use `recalculate` instead. Calls the legacy /apiv2 endpoint.
   */
  calculateInterestsLegacy(id: number): void {
    this.invoicesResource.calculateInterests(id).subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
    });
  }

  recalculate(id: number): void {
    this.invoicesService.recalculateInvoice({ id: id }).subscribe((updated: Invoice) => {
      this.invoiceChanged(updated);
    });
  }

  updateLastPaymentDateFromArex(id: number): void {
    this.invoicesResource
      .updateLastPaymentDateFromArex(id)
      .subscribe((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
      });
  }

  save(invoice: LegacyInvoice): Observable<boolean> {
    return this.invoicesResource.save(invoice).pipe(
      tap((updated: LegacyInvoice) => {
        this.legacyInvoiceChanged(updated);
        this.state.next('unlocked');
      }),
      map(() => true)
    );
  }

  // update legacy invoice observer and fetch invoice from api
  legacyInvoiceChanged(legacyInvoice: LegacyInvoice): void {
    this.legacyInvoice.next(legacyInvoice);

    this.invoicesService
      .getInvoice({ id: legacyInvoice.invoiceId })
      .subscribe((invoice: Invoice) => {
        this.invoice.next(invoice);
      });
  }

  // update invoice observer and fetch legacy invoice from api
  invoiceChanged(invoice: Invoice): void {
    this.invoice.next(invoice);

    this.invoicesResource.get(invoice.id).subscribe({
      next: (legacyInvoice: LegacyInvoice) => {
        this.legacyInvoice.next(legacyInvoice);
      },
      error: (err) => {
        // AI: This is expected to fail for new entities like Light Entrepreneur invoices
        // that don't exist in the legacy system. We can ignore the error and just
        // push null to the legacy stream.
        console.warn('AI: Could not fetch legacy invoice, this is expected for new entities.', err);
        this.legacyInvoice.next(null);
      }
    });
  }
}
